/**
 * F45 (Wave 2D) — notification-engagement page.
 *
 * Route: `/analytics/notifications`. Permission: `analytics:read`.
 *
 * Open/tap rates by category × channel (A7 mapping: opened = delivery_state='read'
 * for push/in_app/whatsapp; email "opened" is NOT tracked → renders "Not tracked").
 * A grouped Bar of open-rate by channel + a drill-down Table. Category + channel
 * Select filters. DateRangePicker bounds the window.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Chart,
  DateRangePicker,
  Select,
  Skeleton,
  Table,
  type DateRange,
  type SelectOption,
  type TableColumn,
} from '@bravemove/ui-web';
import {
  ANALYTICS_NOTIFICATION_CHANNELS,
  type AnalyticsNotificationChannel,
  type AnalyticsNotificationEngagement,
  type AnalyticsNotificationEngagementRow,
} from '@bravemove/types';
import { adminAnalyticsApi } from '../../api/analytics';
import { PageHeader } from '../../components/PageHeader';
import { AnalyticsNav } from './AnalyticsNav';
import { defaultAnalyticsRange, type AnalyticsRangeValue } from './analyticsHelpers';

function ratePct(v: number | null): string | null {
  if (v === null) return null;
  return `${Math.round(v * 1000) / 10}%`;
}

export function AnalyticsNotificationsPage() {
  const { t, locale } = useI18n();

  const [range, setRange] = useState<AnalyticsRangeValue>(() => defaultAnalyticsRange());
  const [category, setCategory] = useState('');
  const [channel, setChannel] = useState('');
  const [engagement, setEngagement] = useState<AnalyticsNotificationEngagement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEngagement = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setEngagement(
        await adminAnalyticsApi.getNotificationEngagement({
          from: range.from,
          to: range.to,
          ...(category ? { category } : {}),
          ...(channel ? { channel: channel as AnalyticsNotificationChannel } : {}),
        }),
      );
    } catch {
      setError(t('admin:analytics.error'));
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, category, channel, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchEngagement();
  }, [fetchEngagement]);

  const rows = useMemo(() => engagement?.rows ?? [], [engagement]);

  const categoryOptions = useMemo<readonly SelectOption[]>(() => {
    const cats = Array.from(new Set(rows.map((r) => r.category)));
    return [
      { value: '', label: t('admin:analytics.notification.allCategories') },
      ...cats.map((c) => ({ value: c, label: c })),
    ];
  }, [rows, t]);

  const channelOptions = useMemo<readonly SelectOption[]>(
    () => [
      { value: '', label: t('admin:analytics.notification.allChannels') },
      ...ANALYTICS_NOTIFICATION_CHANNELS.map((c) => ({
        value: c,
        label: t(`admin:analytics.notification.channelLabel.${c}`),
      })),
    ],
    [t],
  );

  const columns = useMemo<readonly TableColumn<AnalyticsNotificationEngagementRow>[]>(
    () => [
      {
        key: 'category',
        header: t('admin:analytics.notification.category'),
        cell: (r) => r.category,
      },
      {
        key: 'channel',
        header: t('admin:analytics.notification.channel'),
        cell: (r) => t(`admin:analytics.notification.channelLabel.${r.channel}`),
      },
      {
        key: 'sent',
        header: t('admin:analytics.notification.sent'),
        align: 'end',
        cell: (r) => <span className="font-mono tabular-nums">{r.sent.toLocaleString()}</span>,
      },
      {
        key: 'delivered',
        header: t('admin:analytics.notification.delivered'),
        align: 'end',
        cell: (r) => <span className="font-mono tabular-nums">{r.delivered.toLocaleString()}</span>,
      },
      {
        key: 'openRate',
        header: t('admin:analytics.notification.openRate'),
        align: 'end',
        cell: (r) => {
          const v = ratePct(r.openRate);
          return v === null ? (
            <span
              className="text-ink-3"
              data-testid={`analytics-notif-opened-nottracked-${r.category}-${r.channel}`}
            >
              {t('admin:analytics.notification.notTracked')}
            </span>
          ) : (
            <span className="font-mono tabular-nums">{v}</span>
          );
        },
      },
      {
        key: 'tapRate',
        header: t('admin:analytics.notification.tapRate'),
        align: 'end',
        cell: (r) => {
          const v = ratePct(r.tapRate);
          return v === null ? (
            <span className="text-ink-3">{t('admin:analytics.notification.notTracked')}</span>
          ) : (
            <span className="font-mono tabular-nums">{v}</span>
          );
        },
      },
    ],
    [t],
  );

  // Grouped bar: open-rate (%) by channel × category. Email open-rate is null →
  // contributes 0 to the bar (the Table makes "not tracked" explicit).
  const chartData = useMemo(
    () =>
      rows.map((r) => ({
        label: `${r.category} · ${t(`admin:analytics.notification.channelLabel.${r.channel}`)}`,
        openRate: r.openRate === null ? 0 : Math.round(r.openRate * 1000) / 10,
      })),
    [rows, t],
  );

  const onCustomRange = (dr: DateRange) =>
    setRange((prev) => ({ from: dr.start ?? prev.from, to: dr.end ?? prev.to }));

  return (
    <div className="space-y-6" data-testid="analytics-notifications-page">
      <PageHeader
        eyebrow="Analytics"
        title={t('admin:analytics.notification.title')}
        subtitle={t('admin:analytics.notification.subtitle')}
      />
      <AnalyticsNav />

      <div className="flex flex-wrap items-end gap-3" data-testid="analytics-notif-controls">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wider text-ink-3">
            {t('admin:analytics.notification.category')}
          </span>
          <Select
            options={categoryOptions}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="analytics-notif-category"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wider text-ink-3">
            {t('admin:analytics.notification.channel')}
          </span>
          <Select
            options={channelOptions}
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            data-testid="analytics-notif-channel"
          />
        </label>
        <DateRangePicker
          value={{ start: range.from, end: range.to }}
          onChange={onCustomRange}
          locale={locale}
        />
      </div>

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading || !engagement ? (
        <Skeleton className="h-72 w-full rounded-xl" data-testid="analytics-notif-loading" />
      ) : (
        <>
          <section className="rounded-xl border border-hairline bg-surface p-4">
            <Chart
              kind="bar"
              data={chartData}
              xKey="label"
              series={[{ key: 'openRate', label: t('admin:analytics.notification.openRate') }]}
              height={280}
            />
          </section>
          <Table
            data={rows as AnalyticsNotificationEngagementRow[]}
            columns={columns as TableColumn<AnalyticsNotificationEngagementRow>[]}
            rowKey={(r) => `${r.category}:${r.channel}`}
            density="compact"
          />
        </>
      )}
    </div>
  );
}

export default AnalyticsNotificationsPage;
